import React, { useCallback, useEffect, useState } from 'react'
import { Button, Image, Select, Space, Table, Tabs, Tag, Typography, message } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { get } from '../../lib/request'
import {
  APPLICATION_STATUS_LABELS,
  ApplicationItem,
  IDENTITY_LABELS,
  LeadItem,
  LEAD_STATUS_LABELS,
  PageData,
  REFERRER_CHANGE_FIELD_LABELS,
  ReferrerChangeItem,
  SOURCE_MODE_LABELS,
  VERIFY_STATUS_LABELS,
  errMsg,
  fmtTime,
} from '../../lib/api'
import ReviewActions from '../../components/ReviewActions'

const { Text } = Typography

/**
 * 统一审核中心（拍板4）：双 tab —— 推荐身份申请 / 新客校验
 * 复用同一审批组件 ReviewActions（通过/驳回+原因，后端统一响应 {status, reviewNote}）
 */
const ReviewCenterPage: React.FC = () => {
  const [tab, setTab] = useState<'applications' | 'verifications' | 'referrerChanges'>('applications')

  // ---- 推荐身份申请 ----
  const [apps, setApps] = useState<ApplicationItem[]>([])
  const [appsTotal, setAppsTotal] = useState(0)
  const [appsPage, setAppsPage] = useState(1)
  const [appStatus, setAppStatus] = useState<string | undefined>(undefined)
  const [appsLoading, setAppsLoading] = useState(false)

  const fetchApps = useCallback(() => {
    setAppsLoading(true)
    get<PageData<ApplicationItem>>('/admin/review-center/applications', {
      params: { page: appsPage, pageSize: 20, ...(appStatus ? { status: appStatus } : {}) },
    })
      .then((d) => {
        setApps(d.items ?? [])
        setAppsTotal(d.total ?? 0)
      })
      .catch((err) => message.warning(errMsg(err, '申请列表加载失败')))
      .finally(() => setAppsLoading(false))
  }, [appsPage, appStatus])

  // ---- 新客校验 ----
  const [vers, setVers] = useState<LeadItem[]>([])
  const [versTotal, setVersTotal] = useState(0)
  const [versPage, setVersPage] = useState(1)
  const [verStatus, setVerStatus] = useState<string>('pending')
  const [versLoading, setVersLoading] = useState(false)

  const fetchVers = useCallback(() => {
    setVersLoading(true)
    get<PageData<LeadItem>>('/admin/review-center/verifications', {
      params: { page: versPage, pageSize: 20, status: verStatus },
    })
      .then((d) => {
        setVers(d.items ?? [])
        setVersTotal(d.total ?? 0)
      })
      .catch((err) => message.warning(errMsg(err, '校验列表加载失败')))
      .finally(() => setVersLoading(false))
  }, [versPage, verStatus])

  // ---- 推荐人资料修改审核（R1） ----
  const [changes, setChanges] = useState<ReferrerChangeItem[]>([])
  const [changesTotal, setChangesTotal] = useState(0)
  const [changesPage, setChangesPage] = useState(1)
  const [changeStatus, setChangeStatus] = useState<string>('pending')
  const [changesLoading, setChangesLoading] = useState(false)

  const fetchChanges = useCallback(() => {
    setChangesLoading(true)
    get<PageData<ReferrerChangeItem>>('/admin/review-center/referrer-changes', {
      params: { page: changesPage, pageSize: 20, status: changeStatus },
    })
      .then((d) => {
        setChanges(d.items ?? [])
        setChangesTotal(d.total ?? 0)
      })
      .catch((err) => message.warning(errMsg(err, '资料修改列表加载失败')))
      .finally(() => setChangesLoading(false))
  }, [changesPage, changeStatus])

  useEffect(() => { fetchApps() }, [fetchApps])
  useEffect(() => { fetchVers() }, [fetchVers])
  useEffect(() => { fetchChanges() }, [fetchChanges])

  // ---- 审批弹窗（双 tab 共用） ----
  const [reviewUrl, setReviewUrl] = useState<string | null>(null)
  const [reviewTitle, setReviewTitle] = useState('')
  const [reviewLabel, setReviewLabel] = useState('')

  const openReview = (title: string, label: string, url: string) => {
    setReviewTitle(title)
    setReviewLabel(label)
    setReviewUrl(url)
  }

  const refreshCurrent = () => {
    if (tab === 'applications') return fetchApps()
    if (tab === 'verifications') return fetchVers()
    return fetchChanges()
  }

  /** 把 changes JSON 的 old/new 值渲染为可读文本（identity 用中文标签） */
  const renderChangeValue = (field: string, val: unknown): string => {
    if (val === null || val === undefined || val === '') return '（空）'
    if (field === 'identity') return IDENTITY_LABELS[String(val)] ?? String(val)
    return String(val)
  }

  const appColumns: ColumnsType<ApplicationItem> = [
    { title: '申请ID', dataIndex: 'id', key: 'id', render: (v: number) => `#${v}` },
    {
      title: '推荐人',
      key: 'referrer',
      render: (_: unknown, r) => `${r.referrer?.name ?? '—'}（${r.referrer?.rid ?? '—'}）`,
    },
    { title: '真实姓名', dataIndex: 'realName', key: 'realName' },
    { title: '手机号', dataIndex: 'phone', key: 'phone' },
    {
      title: '身份',
      dataIndex: 'identity',
      key: 'identity',
      render: (v: string) => IDENTITY_LABELS[v] ?? v,
    },
    { title: '所在单元', dataIndex: 'unit', key: 'unit', render: (v?: string | null) => v ?? '—' },
    {
      title: '事由',
      dataIndex: 'reason',
      key: 'reason',
      ellipsis: true,
      render: (v?: string | null) => v ?? '—',
    },
    {
      title: '证明材料',
      dataIndex: 'proofImageUrl',
      key: 'proofImageUrl',
      width: 110,
      render: (v?: string | null) =>
        v ? (
          <Image
            src={v}
            alt="证明材料"
            width={64}
            height={64}
            style={{ objectFit: 'cover', borderRadius: 4 }}
            preview={{ mask: '查看' }}
          />
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>—</Text>
        ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (v: string, r) => (
        <>
          <Tag color={APPLICATION_STATUS_LABELS[v]?.color ?? 'default'}>
            {APPLICATION_STATUS_LABELS[v]?.label ?? v}
          </Tag>
          {r.reviewNote && (
            <Text type="secondary" style={{ fontSize: 12 }} title={r.reviewNote}>
              {r.reviewNote}
            </Text>
          )}
        </>
      ),
    },
    { title: '申请时间', dataIndex: 'createdAt', key: 'createdAt', render: fmtTime },
    {
      title: '操作',
      key: 'actions',
      fixed: 'right',
      render: (_: unknown, r) =>
        r.status === 'pending' ? (
          <Button
            size="small"
            type="primary"
            style={{ minHeight: 32 }}
            onClick={() =>
              openReview(
                '推荐身份申请审批',
                `${r.realName}（${r.phone}）`,
                `/admin/review-center/applications/${r.id}/review`,
              )
            }
          >
            审批
          </Button>
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {r.reviewedAt ? `${fmtTime(r.reviewedAt)} 审结` : '已审结'}
          </Text>
        ),
    },
  ]

  const verColumns: ColumnsType<LeadItem> = [
    { title: '线索ID', dataIndex: 'id', key: 'id', render: (v: number) => `#${v}` },
    { title: '看房人', dataIndex: 'name', key: 'name' },
    { title: '手机号', dataIndex: 'phone', key: 'phone' },
    { title: '公司', dataIndex: 'company', key: 'company', render: (v?: string | null) => v ?? '—' },
    {
      title: '来源',
      dataIndex: 'sourceMode',
      key: 'sourceMode',
      render: (v: string) => SOURCE_MODE_LABELS[v] ?? v,
    },
    {
      title: '推荐人',
      key: 'referrer',
      render: (_: unknown, r) => (r.referrer ? `${r.referrer.name}（${r.referrer.rid}）` : '—'),
    },
    {
      title: '业务状态',
      dataIndex: 'status',
      key: 'status',
      render: (v: string) => (
        <Tag color={LEAD_STATUS_LABELS[v as keyof typeof LEAD_STATUS_LABELS]?.color ?? 'default'}>
          {LEAD_STATUS_LABELS[v as keyof typeof LEAD_STATUS_LABELS]?.label ?? v}
        </Tag>
      ),
    },
    {
      title: '校验状态',
      dataIndex: 'verificationStatus',
      key: 'verificationStatus',
      render: (v: string) => (
        <Tag color={VERIFY_STATUS_LABELS[v]?.color ?? 'default'}>{VERIFY_STATUS_LABELS[v]?.label ?? v}</Tag>
      ),
    },
    { title: '创建时间', dataIndex: 'createdAt', key: 'createdAt', render: fmtTime },
    {
      title: '操作',
      key: 'actions',
      fixed: 'right',
      render: (_: unknown, r) =>
        r.verificationStatus === 'pending' ? (
          <Button
            size="small"
            type="primary"
            style={{ minHeight: 32 }}
            onClick={() =>
              openReview(
                '新客校验人工审核',
                `${r.name}（${r.phone}）`,
                `/admin/review-center/verifications/${r.id}/review`,
              )
            }
          >
            审核
          </Button>
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>已审结</Text>
        ),
    },
  ]

  const changeColumns: ColumnsType<ReferrerChangeItem> = [
    { title: '申请ID', dataIndex: 'id', key: 'id', render: (v: number) => `#${v}` },
    {
      title: '推荐人',
      key: 'referrer',
      render: (_: unknown, r) => `${r.referrer?.name ?? '—'}（${r.referrer?.rid ?? '—'}）`,
    },
    {
      title: '修改内容（原值 → 新值）',
      key: 'changes',
      render: (_: unknown, r) => (
        <Space direction="vertical" size={2}>
          {Object.entries(r.changes ?? {}).map(([field, v]) => (
            <span key={field} style={{ fontSize: 13 }}>
              <Text type="secondary">{REFERRER_CHANGE_FIELD_LABELS[field] ?? field}：</Text>
              <Text delete type="secondary">{renderChangeValue(field, v?.old)}</Text>
              <Text type="secondary"> → </Text>
              <Text strong style={{ color: '#1A873B' }}>{renderChangeValue(field, v?.new)}</Text>
            </span>
          ))}
        </Space>
      ),
    },
    {
      title: '证明材料',
      dataIndex: 'proofImageUrl',
      key: 'proofImageUrl',
      width: 110,
      render: (v?: string | null) =>
        v ? (
          <Image
            src={v}
            alt="证明材料"
            width={64}
            height={64}
            style={{ objectFit: 'cover', borderRadius: 4 }}
            preview={{ mask: '查看' }}
          />
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>—</Text>
        ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (v: string, r) => (
        <>
          <Tag color={APPLICATION_STATUS_LABELS[v]?.color ?? 'default'}>
            {APPLICATION_STATUS_LABELS[v]?.label ?? v}
          </Tag>
          {r.reviewNote && (
            <Text type="secondary" style={{ fontSize: 12 }} title={r.reviewNote}>
              {r.reviewNote}
            </Text>
          )}
        </>
      ),
    },
    { title: '提交时间', dataIndex: 'createdAt', key: 'createdAt', render: fmtTime },
    {
      title: '操作',
      key: 'actions',
      fixed: 'right',
      render: (_: unknown, r) =>
        r.status === 'pending' ? (
          <Button
            size="small"
            type="primary"
            style={{ minHeight: 32 }}
            onClick={() =>
              openReview(
                '推荐人资料修改审批',
                `${r.referrer?.name ?? '—'}（${r.referrer?.rid ?? '—'}）`,
                `/admin/review-center/referrer-changes/${r.id}/review`,
              )
            }
          >
            审批
          </Button>
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {r.reviewedAt ? `${fmtTime(r.reviewedAt)} 审结` : '已审结'}
          </Text>
        ),
    },
  ]

  return (
    <>
      <Tabs
        activeKey={tab}
        onChange={(k) => setTab(k as typeof tab)}
        items={[
          {
            key: 'applications',
            label: '推荐身份申请',
            children: (
              <>
                <Space style={{ marginBottom: 14 }} wrap>
                  <Text type="secondary">状态筛选：</Text>
                  <Select
                    allowClear
                    placeholder="全部状态"
                    style={{ minWidth: 140 }}
                    value={appStatus}
                    onChange={(v) => { setAppStatus(v); setAppsPage(1) }}
                    options={Object.entries(APPLICATION_STATUS_LABELS).map(([value, s]) => ({
                      value, label: s.label,
                    }))}
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    通过时后端自动比对黑名单与 HR 花名册，命中即拒绝
                  </Text>
                </Space>
                <Table<ApplicationItem>
                  rowKey="id"
                  loading={appsLoading}
                  columns={appColumns}
                  dataSource={apps}
                  scroll={{ x: 'max-content' }}
                  style={{ background: '#fff', borderRadius: 12 }}
                  pagination={{
                    current: appsPage, pageSize: 20, total: appsTotal,
                    showTotal: (t) => `共 ${t} 条`, onChange: (p) => setAppsPage(p),
                  }}
                />
              </>
            ),
          },
          {
            key: 'verifications',
            label: '新客校验',
            children: (
              <>
                <Space style={{ marginBottom: 14 }} wrap>
                  <Text type="secondary">校验状态：</Text>
                  <Select
                    style={{ minWidth: 140 }}
                    value={verStatus}
                    onChange={(v) => { setVerStatus(v); setVersPage(1) }}
                    options={Object.entries(VERIFY_STATUS_LABELS).map(([value, s]) => ({
                      value, label: s.label,
                    }))}
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    通过 = 确认为新客户（写 verifiedBy/verifiedAt）；不改变业务状态机
                  </Text>
                </Space>
                <Table<LeadItem>
                  rowKey="id"
                  loading={versLoading}
                  columns={verColumns}
                  dataSource={vers}
                  scroll={{ x: 'max-content' }}
                  style={{ background: '#fff', borderRadius: 12 }}
                  pagination={{
                    current: versPage, pageSize: 20, total: versTotal,
                    showTotal: (t) => `共 ${t} 条`, onChange: (p) => setVersPage(p),
                  }}
                />
              </>
            ),
          },
          {
            key: 'referrerChanges',
            label: '信息修改审核',
            children: (
              <>
                <Space style={{ marginBottom: 14 }} wrap>
                  <Text type="secondary">状态筛选：</Text>
                  <Select
                    style={{ minWidth: 140 }}
                    value={changeStatus}
                    onChange={(v) => { setChangeStatus(v); setChangesPage(1) }}
                    options={Object.entries(APPLICATION_STATUS_LABELS).map(([value, s]) => ({
                      value, label: s.label,
                    }))}
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    通过后新资料即时生效并写回推荐人；驳回则维持原值
                  </Text>
                </Space>
                <Table<ReferrerChangeItem>
                  rowKey="id"
                  loading={changesLoading}
                  columns={changeColumns}
                  dataSource={changes}
                  scroll={{ x: 'max-content' }}
                  style={{ background: '#fff', borderRadius: 12 }}
                  pagination={{
                    current: changesPage, pageSize: 20, total: changesTotal,
                    showTotal: (t) => `共 ${t} 条`, onChange: (p) => setChangesPage(p),
                  }}
                />
              </>
            ),
          },
        ]}
      />
      <ReviewActions
        title={reviewTitle}
        reviewUrl={reviewUrl}
        targetLabel={reviewLabel}
        onClose={() => setReviewUrl(null)}
        onDone={refreshCurrent}
      />
    </>
  )
}

export default ReviewCenterPage
